import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "../stores/uiStore";
import { getInventoryDivergences, reviewDivergences, unreviewDivergences, type Disposition } from "../api/rest";
import type { DivergenceRow } from "../api/rest";
import styles from "./Panel.module.css";

// Same generic pill palette as the run report's DIV_STATUS_PILL, mapped onto
// the Panel module's classes (this panel does not load the report's CSS).
const PILL: Record<Exclude<DivergenceRow["status"], "match">, string> = {
  qty_mismatch: "pill_idle",
  missing_in_ic3: "pill_linkdown",
  unexpected_in_ic3: "pill_empty",
  // ADR 384: explained by the closing snapshot — the calm pill, nothing to act on.
  received_in_window: "pill_empty",
  shipped_in_window: "pill_empty",
};
const LABEL: Record<Exclude<DivergenceRow["status"], "match">, string> = {
  qty_mismatch: "inventory.wms.qtyMismatch",
  missing_in_ic3: "inventory.wms.missing",
  unexpected_in_ic3: "inventory.wms.unexpected",
  received_in_window: "inventory.wms.receivedInWindow",
  shipped_in_window: "inventory.wms.shippedInWindow",
};

/**
 * ADR 287: the divergence lens of the bin inspector — what the run report
 * (ADR 283) knows about THIS address, readable and justifiable without
 * leaving /live. Reads the same run-scoped divergences fetch as the report
 * (shared query key, one cache) and filters to the address; renders nothing
 * when the address has no divergence (ADR 227 rule: absent, not empty).
 *
 * The run to justify against is the cell's own `runId` — under a multi-run
 * merge, the per-address winner (ADR 198). A `not_scanned` cell has none;
 * it falls back to the picker's selection only when that names exactly one
 * run, because guessing among several would justify against the wrong report.
 */
export function BinDivergenceSection({ siteId, warehouseId, address, runId }: {
  siteId: string; warehouseId: string; address: string; runId: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const runSelection = useUIStore((s) => s.runSelection);
  const effectiveRunId = runId
    ?? (runSelection && runSelection.warehouseId === warehouseId && runSelection.runIds.length === 1
      ? runSelection.runIds[0]
      : null);

  const div = useQuery({
    // Deliberately the run page's key ("inventory-div"): justifying on either
    // surface invalidates and refreshes both.
    queryKey: ["inventory-div", siteId, effectiveRunId],
    queryFn: () => getInventoryDivergences(siteId, effectiveRunId!),
    enabled: !!siteId && !!effectiveRunId,
    staleTime: 60_000,
    retry: false,
  });

  const [justifyProduct, setJustifyProduct] = useState<string | null>(null);
  const [justification, setJustification] = useState("");
  const [disposition, setDisposition] = useState<Disposition>("accepted");
  const review = useMutation({
    mutationFn: (product: string) =>
      reviewDivergences(siteId, effectiveRunId!, { justification, rows: [{ address, product }], disposition }),
    onSuccess: () => {
      setJustifyProduct(null);
      setJustification("");
      void qc.invalidateQueries({ queryKey: ["inventory-div", siteId, effectiveRunId] });
    },
  });
  const unreview = useMutation({
    mutationFn: (product: string) =>
      unreviewDivergences(siteId, effectiveRunId!, [{ address, product }]),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["inventory-div", siteId, effectiveRunId] }),
  });

  const rows = (div.data?.rows ?? []).filter(
    (r): r is DivergenceRow & { status: keyof typeof PILL } =>
      r.address === address && r.status !== "match");
  if (!effectiveRunId || rows.length === 0) return null;

  const qty = (v: number | string | null | undefined) =>
    v == null ? "—" : String(v).replace(".", ",");
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "4px 6px", marginTop: 2,
    background: "var(--op-surface)", color: "var(--op-ink-900)",
    border: "1px solid var(--op-line)", borderRadius: 4, fontFamily: "monospace",
  };

  return (
    <section
      data-testid="bin-divergence"
      style={{ marginTop: "var(--space-3)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--op-line)" }}
    >
      <strong style={{ fontSize: 13 }}>{t("inspector.bin.divergence")}</strong>
      {rows.map((r) => (
        <div key={r.product} style={{ marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span className={styles.mono} style={{ fontSize: 12 }}>{r.product || "—"}</span>
            <span className={`${styles.pill} ${styles[PILL[r.status]]}`}>{t(LABEL[r.status])}</span>
            {r.review && (
              <span className={`${styles.pill} ${styles.pill_loaded}`}>
                {t(r.review.disposition === "recount" ? "inventory.review.disposition.recount"
                  : r.review.disposition === "adjust_wms" ? "inventory.review.disposition.adjust_wms"
                  : "inventory.review.justified")}
              </span>
            )}
          </div>
          {r.status === "qty_mismatch" && (
            <p className={styles.mono} style={{ fontSize: 11, margin: "4px 0 0" }}>
              {t("inventory.wms.col.expected")}: {qty(r.expectedQty)} · {t("inventory.wms.col.counted")}: {qty(r.countedQty)}
            </p>
          )}
          {r.review ? (
            <>
              <p style={{ fontSize: 12, margin: "4px 0 0" }}>{r.review.justification}</p>
              <p className={styles.mono} style={{ fontSize: 11, margin: "2px 0 0", color: "var(--op-ink-500)" }}>
                {r.review.reviewedBy} · {new Date(r.review.reviewedAt).toLocaleDateString(i18n.language)}
              </p>
              <button
                type="button" className={styles.btn} style={{ marginTop: 6 }}
                disabled={unreview.isPending}
                onClick={() => unreview.mutate(r.product)}
              >
                {t("inventory.review.undo")}
              </button>
            </>
          ) : justifyProduct === r.product ? (
            <div style={{ marginTop: 6 }}>
              <label style={{ fontSize: 11 }} htmlFor={`bin-justification-${r.product}`}>
                {t("inventory.review.promptOne", { address, product: r.product || "—" })}
              </label>
              <textarea
                id={`bin-justification-${r.product}`}
                style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
                rows={2}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", fontSize: 11 }}>
                {(["accepted", "recount", "adjust_wms"] as const).map((d) => (
                  <label key={d} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                    <input type="radio" name={`bin-disposition-${r.product}`} checked={disposition === d}
                           onChange={() => setDisposition(d)} />
                    {t(`inventory.review.disposition.${d}`)}
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button
                  type="button" className={styles.btn}
                  disabled={!justification.trim() || review.isPending}
                  onClick={() => review.mutate(r.product)}
                >
                  {t("inventory.review.confirm")}
                </button>
                <button type="button" className={styles.btn} onClick={() => setJustifyProduct(null)}>
                  {t("common.cancel")}
                </button>
              </div>
              {review.isError && (
                <p style={{ fontSize: 11, color: "var(--op-danger)", marginTop: 4 }}>{t("inventory.review.error")}</p>
              )}
            </div>
          ) : (
            <button
              type="button" className={styles.btn} style={{ marginTop: 6 }}
              onClick={() => { setJustifyProduct(r.product); setJustification(""); }}
            >
              {t("inventory.review.one")}
            </button>
          )}
        </div>
      ))}
    </section>
  );
}
